import { Box, Stack, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { RobotOutlined } from '@ant-design/icons';
import { ChatCard } from './types';

interface Props {
  cards: ChatCard[];
  children: React.ReactNode;
}

const kindOf = (type: ChatCard['type']): 'user' | 'assistant' => {
  return type === 'user_message' ? 'user' : 'assistant';
};

export function groupCardsByTurn(cards: ChatCard[]): ChatCard[][] {
  const groups: ChatCard[][] = [];
  let current: ChatCard[] = [];
  let currentKind: 'user' | 'assistant' | null = null;

  for (const card of cards) {
    const kind = kindOf(card.type);
    if (kind !== currentKind) {
      if (current.length > 0) groups.push(current);
      current = [];
      currentKind = kind;
    }
    current.push(card);
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

const TurnContainer = ({ cards, children }: Props) => {
  const theme = useTheme();
  const firstCard = cards[0];
  if (!firstCard) return null;
  if (kindOf(firstCard.type) === 'user') {
    return <Box sx={{ mb: 1.5 }}>{children}</Box>;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        mb: 2.5,
        pl: 3.25,
        pr: 0.25,
      }}
    >
      <Stack direction="row" spacing={0.875} alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{
          width: 22, height: 22, borderRadius: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.15),
          color: theme.palette.primary.main,
          position: 'absolute',
          left: 0,
          top: 0,
        }}>
          <RobotOutlined style={{ fontSize: 12 }} />
        </Box>
        <Typography sx={{
          fontWeight: 700,
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: 1.1,
          color: theme.palette.text.secondary,
        }}>
          AI Analysis
        </Typography>
      </Stack>

      <Box
        sx={{
          position: 'absolute',
          left: 10,
          top: 26,
          bottom: 4,
          width: '2px',
          borderRadius: 1,
          background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        }}
      />

      <Box>{children}</Box>
    </Box>
  );
};

export default TurnContainer;
